import * as React from 'react';
import {
  List,
  Datagrid,
  FunctionField,
  useNotify,
  Confirm,
  useRefresh,
  useListContext,
  useDelete,
} from 'react-admin';
import { Box, Chip, Tooltip, Typography, useMediaQuery } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { IconButton } from '../../components/icon-button/IconButton';
import { CardActionIcons } from '../../components/card-actions/CardActions';
import { EmptyBox } from '../../components/empty-box/EmptyBox';
import { PageHeader } from '../../components/page-header/PageHeader';
import { BackButton } from '../../components/back-button/BackButton';
import { listSurfaceSx } from '../../core/theme/listStyles';
import { cardActionButtonSx } from '../../core/theme/buttonStyles';
import { Coupon, formatDiscount, formatMoney } from './couponTypes';
import type { TFunction } from 'i18next';
import { AccessibleLocation, useAccessibleLocations } from '../../hooks/useAccessibleLocations';

type CouponStatus = 'active' | 'usedUp' | 'inactive';

const couponStatus = (coupon: Coupon): CouponStatus => {
  if (!coupon.isActive) return 'inactive';
  if (coupon.usedCount >= coupon.usageLimit) return 'usedUp';
  return 'active';
};

const StatusChip = ({ coupon }: { coupon: Coupon }) => {
  const { t } = useTranslation();
  const status = couponStatus(coupon);
  return (
    <Chip
      size="small"
      label={t(`coupons.status.${status}`)}
      color={status === 'active' ? 'primary' : 'default'}
      sx={status === 'usedUp' ? { backgroundColor: 'var(--color-warning-soft)', color: 'var(--color-warning)' } : undefined}
    />
  );
};

const usageLabel = (coupon: Coupon) => `${coupon.usedCount} / ${coupon.usageLimit}`;

const customerRuleLabel = (coupon: Coupon, t: TFunction) => {
  if (coupon.firstOrderOnly) return t('coupons.customerRules.firstOrder');
  if (coupon.perCustomerLimit === 1) return t('coupons.customerRules.oneTime');
  if (coupon.perCustomerLimit != null) {
    return t('coupons.customerRules.perCustomer', { limit: coupon.perCustomerLimit });
  }
  return null;
};

// Etykieta ograniczenia dla klienta przy kodzie (jak w DiscountsList); bez ograniczenia nic.
const CustomerRuleChip = ({ coupon }: { coupon: Coupon }) => {
  const { t } = useTranslation();
  const label = customerRuleLabel(coupon, t);
  if (!label) return null;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        backgroundColor: 'var(--color-bg-muted)',
        color: 'var(--color-text-secondary)',
        flexShrink: 0,
      }}
    />
  );
};

const minOrderLabel = (coupon: Coupon, locations: AccessibleLocation[]) =>
  coupon.minOrderAmount != null ? formatMoney(coupon.minOrderAmount, locationCurrency(coupon, locations)) : '—';

const locationName = (coupon: Coupon, locations: AccessibleLocation[]) =>
  locations.find((l) => l.id === coupon.locationId)?.name ?? coupon.locationName ?? '—';

const locationCurrency = (coupon: Coupon, locations: AccessibleLocation[]) =>
  locations.find((l) => l.id === coupon.locationId)?.currency ?? 'PLN';

const CouponsListHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  return (
    <PageHeader
      stretchActionsOnMobile
      back={<BackButton onClick={() => navigate('/discount-types')} />}
      title={t('discountsSection.kinds.coupons.title')}
      subtitle={t('coupons.subtitle')}
      actions={
        <IconButton
          icon={<AddIcon />}
          label={t('coupons.addCoupon')}
          sx={isMobile ? cardActionButtonSx : undefined}
          onClick={() => navigate('/coupons/create')}
        />
      }
    />
  );
};

interface ListBodyProps {
  locations: AccessibleLocation[];
  onDelete: (coupon: Coupon) => void;
}

const MobileCouponsList = ({ locations, onDelete }: ListBodyProps) => {
  const { data, isLoading } = useListContext<Coupon>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (isLoading || !data) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {data.map((coupon) => (
        <Box
          key={coupon.id}
          sx={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            p: 'var(--space-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontWeight: 700, fontSize: 'var(--text-sm)', fontFamily: 'monospace', flex: 1, minWidth: 0 }}
            >
              {coupon.code}
            </Typography>
            <CustomerRuleChip coupon={coupon} />
            <StatusChip coupon={coupon} />
            <CardActionIcons
              actions={[
                {
                  key: 'edit',
                  label: t('common.edit'),
                  icon: <EditIcon fontSize="small" />,
                  onClick: () => navigate(`/coupons/${coupon.id}`),
                },
                {
                  key: 'delete',
                  label: t('common.delete'),
                  icon: <DeleteOutlineIcon fontSize="small" />,
                  destructive: true,
                  onClick: () => onDelete(coupon),
                },
              ]}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 'var(--space-4)',
              rowGap: 'var(--space-1)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <span>{formatDiscount(coupon, locationCurrency(coupon, locations))}</span>
            <span>
              {t('coupons.columns.minOrderAmount')}: {minOrderLabel(coupon, locations)}
            </span>
            <span>
              {t('coupons.columns.usage')}: {usageLabel(coupon)}
            </span>
            <span>
              {t('coupons.columns.combinable')}: {coupon.combinableWithDiscounts ? t('common.yes') : t('common.no')}
            </span>
            <span>{locationName(coupon, locations)}</span>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const DesktopCouponsList = ({ locations, onDelete }: ListBodyProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Datagrid
      bulkActionButtons={false}
      rowClick={false}
      sx={{ '& .RaDatagrid-headerCell': { fontWeight: 700, letterSpacing: 0.2 } }}
    >
      <FunctionField
        sortable={false}
        label={t('coupons.columns.code')}
        render={(c: Coupon) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 'var(--text-sm)' }}>
              {c.code}
            </Typography>
            <CustomerRuleChip coupon={c} />
          </Box>
        )}
      />
      <FunctionField
        sortable={false}
        label={t('coupons.columns.location')}
        render={(c: Coupon) => locationName(c, locations)}
      />
      <FunctionField
        sortable={false}
        label={t('coupons.columns.discount')}
        render={(c: Coupon) => formatDiscount(c, locationCurrency(c, locations))}
      />
      <FunctionField
        sortable={false}
        label={t('coupons.columns.minOrderAmount')}
        render={(c: Coupon) => minOrderLabel(c, locations)}
      />
      <FunctionField sortable={false} label={t('coupons.columns.usage')} render={usageLabel} />
      <FunctionField
        sortable={false}
        label={t('coupons.columns.combinable')}
        render={(c: Coupon) => (c.combinableWithDiscounts ? t('common.yes') : t('common.no'))}
      />
      <FunctionField
        sortable={false}
        label={t('coupons.columns.status')}
        render={(c: Coupon) => <StatusChip coupon={c} />}
      />
      <FunctionField
        label=""
        sortable={false}
        render={(c: Coupon) => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title={t('common.edit')}>
              <span>
                <IconButton icon={<EditIcon />} onClick={() => navigate(`/coupons/${c.id}`)} />
              </span>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <span>
                <IconButton icon={<DeleteOutlineIcon />} color="var(--color-error)" onClick={() => onDelete(c)} />
              </span>
            </Tooltip>
          </Box>
        )}
      />
    </Datagrid>
  );
};

export const CouponsList = () => {
  const { t } = useTranslation();
  const notify = useNotify();
  const refresh = useRefresh();
  const isMobile = useMediaQuery('(max-width:600px)');
  const { locations } = useAccessibleLocations();
  const [deleteOne] = useDelete();
  const [toDelete, setToDelete] = React.useState<Coupon | null>(null);

  return (
    <>
      <CouponsListHeader />
      <List
        resource="coupons"
        actions={false}
        perPage={25}
        empty={
          <EmptyBox
            title={t('coupons.emptyBox.title')}
            description={t('coupons.emptyBox.description')}
            resource="coupons"
          />
        }
        sx={listSurfaceSx({ isMobile })}
      >
        {isMobile ? (
          <MobileCouponsList locations={locations} onDelete={setToDelete} />
        ) : (
          <DesktopCouponsList locations={locations} onDelete={setToDelete} />
        )}
      </List>

      <Confirm
        isOpen={toDelete !== null}
        title={t('coupons.dialog.confirmDeletionTitle')}
        content={t('coupons.dialog.confirmDeletionBody', { code: toDelete?.code ?? '' })}
        confirm={t('common.delete')}
        cancel={t('common.cancel')}
        onConfirm={async () => {
          if (!toDelete) return;
          try {
            await deleteOne('coupons', { id: toDelete.id, previousData: toDelete }, { returnPromise: true });
            notify(t('coupons.notifications.deleted'), { type: 'success' });
            refresh();
          } catch (error: any) {
            // axiosHttpClient w dataProviderze odrzuca { status, message, body } — tresc bledu
            // z backendu ({ error: ... }) siedzi w `body`.
            notify(error?.body?.error ?? error?.message, { type: 'error' });
            Sentry.captureException(error);
          } finally {
            setToDelete(null);
          }
        }}
        onClose={() => setToDelete(null)}
      />
    </>
  );
};

export default CouponsList;
